// The grammar terms (Kielioppi upgrade step 1, Axel 2026-10-07): one shared list, so every page
// explains a term the same way. Where a term first appears in a section, <Define id> writes its
// short sentence (and the Finnish comparison) inline; everywhere else <Term id>…</Term> makes the
// word tappable, and the same entry opens in the margin (PC) or the term sheet (phone).
//
// Entries live in `*.terms.ts` files next to this one, one per area (sounds, nouns, verbs…),
// collected by ./index.ts with import.meta.glob: a new term is a line in its area's file, nothing
// central changes. Contract for authors: ../AUTHORING.md "Terms".
//
// Inline markup in the texts (./markup.tsx): *word* = a Finnish or English word in italics,
// **term** = the term being defined (bold; inside <Define> it is the <dfn>), [[ā]] = High Valyrian
// (the HV face, never a link, never translated): only an ending, a letter or a sound, or a word the
// lessons or Kielioppi already write in <Hv> (test/grammar-terms.test.tsx checks every one).

/** A term in one UI language. */
export interface TermText {
  /** The term in its base form, lower case unless a name ("diftongi", "I-luokka"). */
  name: string;
  /** One sentence: what it is, plainly. Inline in <Define>, first in the sheet. */
  short: string;
  /** One sentence comparing with Finnish, or saying plainly that Finnish has no such thing. */
  compare?: string;
  /** More, for the sheet only: 2–4 sentences at most. HV-specific claims only as Kielioppi states them. */
  long?: string;
}

export interface TermDef {
  fi: TermText;
  en: TermText;
  /** Other term ids worth reading next ("Katso myös"). */
  related?: readonly string[];
  /** The Kielioppi topic id where the term is explained in full (the sheet links there). */
  firstIn?: string;
}

/** A file's terms: id (lower case ASCII, hyphens: "pitka-vokaali") → entry. */
export const defineTerms = <K extends string>(terms: Record<K, TermDef>): Record<K, TermDef> => terms;
